import { deviceHeight } from "@/app/activity/components/timeline/activityTimeline/constants"
import { useGetWidthPerTime } from "@/app/activity/components/timeline/activityTimeline/hooks"
import { getXPositionByDate } from "@/app/activity/components/timeline/activityTimeline/utils"
import { speedUnitLimits } from "@/app/activity/components/deviceTimeline/constants"
import { DeviceCropTimelineProps } from "@/app/activity/components/deviceTimeline/props"
import { DrawChartParams } from "@/app/activity/components/deviceTimeline/type"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"
import { useSpeedDataQuery } from "@/hooks/api/useDataQuery"
import { createGlobalStore } from "@/stores/global"

import classNames from "classnames"
import { useEffect, useMemo, useRef, useState } from "react"

import "../deviceTimeline.scss"
import DeviceCropItemBox from "@/app/activity/components/deviceTimeline/deviceCropTimeline/deviceCropItemBox/DeviceCropItemBox"
import { pointer } from "d3-selection"
import { clamp, isNil } from "lodash"
import { getGuideLineDate, getGuideLinePosition } from "@/app/activity/components/utils/util.ts"
import { colors } from "@/components/colors.ts"
import { CropType, LocalSessionCropData } from "@/app/activity/detail/type.ts"
import { SessionCropData } from "@/types/session.ts"
import { format, isAfter, isBefore, max, min } from "date-fns"
import { HOUR_TO_SECOND_FORMAT } from "@/constants/regex.ts"
import { createSessionCropStore } from "@/app/activity/store/sessionCropStore"

const speedChartPadding = 5

export default function DeviceCropTimeline({
  activityData,
  className,
  sessionCropDataList,
  isCopying,
  idfSession,
  localCropDataList,
  tempCropDataList,
  lastCropAction,
  addHistoryStack,
  popHistoryStack,
  addCropDataToLastHistory,
  popHistoryStackDirect,
  setDeleteHistory,
  ...props
}: DeviceCropTimelineProps) {
  const ref = useRef<SVGSVGElement>(null)
  const [points, setPoints] = useState<string>("")
  const [isIntersect, setIsIntersect] = useState<boolean>(false)
  const [mouseGuidelineX, setMouseGuidelineX] = useState(0)
  const [mouseGuidelineDate, setMouseGuidelineDate] = useState<Date>()

  const [width, setWidth] = useState(1)

  const [isMouseOver, setIsMouseOver] = useState(false)
  const [tempStartX, setTempStartX] = useState<number | undefined>(undefined)
  const [tempEndX, setTempEndX] = useState<number | undefined>(undefined)
  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(undefined)
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(undefined)

  const [isEscapePressed, setIsEscapePressed] = useState(false)
  const [lastProcessedCopyAll, setLastProcessedCopyAll] = useState<string | null>(null)

  const { data: speedDataResponse } = useSpeedDataQuery(activityData.idfData, isIntersect)

  const { teamUnit } = createGlobalStore((state) => ({
    teamUnit: state.teamUnit,
  }))

  const { tempCropStartDate, tempCropEndDate, activeCropIdfData } = createSessionCropStore(
    (state) => ({
      tempCropStartDate: state.tempCropStartDate,
      tempCropEndDate: state.tempCropEndDate,
      activeCropIdfData: state.activeCropIdfData,
    }),
  )

  const { session, sessions, sessionDataList } = createSessionStore((state) => ({
    session: state.session,
    sessions: state.sessions,
    sessionDataList: state.sessionDataList,
  }))

  const { setTempCropStartDate, setTempCropEndDate, setActiveCropIdfData } = createSessionCropStore(
    (state) => ({
      setTempCropStartDate: state.setTempCropStartDate,
      setTempCropEndDate: state.setTempCropEndDate,
      setActiveCropIdfData: state.setActiveCropIdfData,
    }),
  )

  const {
    timePerWidth,
    timelineWidth,
    remainRightArea = 0,
    sessionMouseGuideLine,
    range,
    magnification,
    setSessionMouseGuideLine,
  } = createActivityCommonStore((state) => ({
    timePerWidth: state.timePerWidth,
    timelineWidth: state.timelineWidth,
    remainRightArea: state.remainingArea,
    sessionMouseGuideLine: state.sessionMouseGuideLine,
    range: state.range,
    magnification: state.magnification,
    setSessionMouseGuideLine: state.setSessionMouseGuideLine,
  }))

  const chartXSpace = useGetWidthPerTime({
    dateStart: (session?.dateStart && new Date(session.dateStart)) || range.dateStart || new Date(),
    dateFinish:
      (session?.dateFinish && new Date(session.dateFinish)) || range.dateFinish || new Date(),
    chartWidth: timelineWidth,
  })

  const sessionCropData = useMemo(() => {
    return sessionDataList.find((sessionItem) => sessionItem.idfData === activityData.idfData)
  }, [sessionDataList, activityData.idfData])

  const teamSpeedUnit = useMemo(() => {
    return teamUnit.find((item) => item.type === "speed")?.unit || "km/h"
  }, [teamUnit])

  const chartDataX = useMemo(() => {
    const { dateStart, dateFinish } = sessionCropData || {}

    if (!dateFinish || !dateStart) {
      return {}
    }

    if (speedDataResponse) {
      const width = timelineWidth
      const startX = getXPositionByDate(
        new Date(speedDataResponse.dateStart),
        new Date(session?.dateStart as string),
        new Date(session?.dateFinish as string),
      )

      const endX = getXPositionByDate(
        new Date(speedDataResponse.dateFinish),
        new Date(session?.dateStart as string),
        new Date(session?.dateFinish as string),
      )

      return {
        start: startX * width,
        end: endX * width,
      }
    }
  }, [speedDataResponse, timelineWidth, session])

  const dataX = useMemo(() => {
    const { dateStart: start, dateFinish: finish } = activityData
    const { dateStart, dateFinish } = sessionCropData || {}

    if (!dateFinish || !dateStart) {
      return {}
    }

    if (speedDataResponse) {
      const width = timelineWidth
      const startX = getXPositionByDate(
        new Date(start),
        new Date(session?.dateStart as string),
        new Date(session?.dateFinish as string),
      )

      const endX = getXPositionByDate(
        new Date(finish),
        new Date(session?.dateStart as string),
        new Date(session?.dateFinish as string),
      )

      return {
        start: startX * width,
        end: endX * width,
      }
    }
  }, [activityData, speedDataResponse, timelineWidth, session])

  const mergedCropDataList = useMemo(() => {
    let result: SessionCropData[] = [...sessionCropDataList]

    localCropDataList?.forEach((item, itemIndex) => {
      switch (item.actionType) {
        case CropType.add:
        case CropType.copyAll:
        case CropType.copy: {
          const filteredHistory = item.history.filter(
            (history) => history.idfData === activityData.idfData,
          )
          result = result.concat(filteredHistory)
          break
        }
        case CropType.delete: {
          result = result.filter((history) => {
            const shouldDelete = item.history.some((h) => {
              if (h.idfData !== history.idfData) {
                return false
              }

              if (h.localIdfCropNumber && history.localIdfCropNumber) {
                return h.localIdfCropNumber === history.localIdfCropNumber
              }

              if (
                h.idfSessionCropRecord &&
                h.idfSessionCropRecord !== 0 &&
                history.idfSessionCropRecord &&
                history.idfSessionCropRecord !== 0
              ) {
                return h.idfSessionCropRecord === history.idfSessionCropRecord
              }

              return false
            })

            return !shouldDelete
          })
          break
        }
      }
    })

    return result
  }, [localCropDataList, tempCropDataList, sessionCropDataList, activityData.idfData])

  const drawSpeedDataByWorker = ({ height, magnification = 1 }: DrawChartParams) => {
    const { limit } = speedUnitLimits[teamSpeedUnit]

    const widthPerTime = chartXSpace * 1000

    if (!speedDataResponse || speedDataResponse.speedData.length === 0) {
      return
    }

    const points = speedDataResponse.speedData.reduce(
      (points, speed, i) =>
        `${points}${i * widthPerTime * magnification},${height - height * (speed / limit)} `,
      "",
    )

    setPoints(points)
  }

  const sessionData = useMemo(() => {
    return sessions.find((sessionItem) => sessionItem.idfSession === idfSession)
  }, [sessions, idfSession])

  const tickStartDate = useMemo(() => {
    const currentItem = session
    const dateStart = new Date(currentItem?.dateStart as string)

    if (remainRightArea && currentItem?.dateStart) {
      return new Date(dateStart.valueOf() - (remainRightArea / 2) * timePerWidth)
    }

    if (currentItem?.dateStart) {
      return dateStart
    }

    return currentItem?.dateStart
  }, [remainRightArea, session?.dateStart, timePerWidth])

  const tickFinishDate = useMemo(() => {
    const currentItem = session
    const dateFinish = new Date(currentItem?.dateFinish as string)

    if (remainRightArea && range.dateFinish) {
      return new Date(dateFinish.valueOf() + (remainRightArea / 2) * timePerWidth)
    }

    if (range.dateFinish) {
      return dateFinish
    }

    return currentItem?.dateFinish
  }, [remainRightArea, session?.dateFinish, timePerWidth])

  const nearestNextCropStartTime = useMemo(() => {
    if (isNil(tempStartDate)) {
      return Number.POSITIVE_INFINITY
    }

    const dateList = sessionCropDataList.map(
      (item) => new Date(item.cropStartTime).valueOf() as number,
    )
    const startDate = tempStartDate.valueOf()

    return Math.min(...dateList.filter((date) => date >= startDate))
  }, [tempStartX, tempStartDate, sessionCropDataList])

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsEscapePressed(true)
    }
  }

  const onMouseEnter = (event: React.MouseEvent<SVGSVGElement | SVGLineElement, MouseEvent>) => {
    const startX = 0
    const endX = width
    const [x] = pointer(event)
    const point = clamp(x, startX + remainRightArea / 2, (endX as number) + remainRightArea / 2)
    setMouseGuidelineX(point)

    setIsMouseOver(true)

    if (range.dateStart && range.dateFinish) {
      getGuideLineDate({
        position: point,
        tickStartDate: tickStartDate as Date,
        tickFinishDate: tickFinishDate as Date,
        width,
        originalWidth: 1,
      })
    }
  }

  const onMouseMove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const startX = 0
    const endX = width
    const [x] = pointer(event)

    const lower =
      tempStartX && !tempEndDate
        ? Math.max(tempStartX, startX + remainRightArea / 2)
        : startX + remainRightArea / 2

    const nearestNextPosition = getGuideLinePosition({
      date: new Date(nearestNextCropStartTime),
      tickStartDate: tickStartDate as Date,
      tickFinishDate: tickFinishDate as Date,
      width,
      originalWidth: 1,
    })

    const higher =
      isNaN(nearestNextPosition) || tempEndDate
        ? (endX as number) + remainRightArea / 2
        : Math.min(nearestNextPosition, (endX as number) + remainRightArea / 2)

    const point = clamp(x, lower, higher)
    setMouseGuidelineX(point)

    setIsMouseOver(true)
    if (range.dateStart && range.dateFinish) {
      setMouseGuidelineDate(
        getGuideLineDate({
          position: point,
          tickStartDate: tickStartDate as Date,
          tickFinishDate: tickFinishDate as Date,
          width,
          originalWidth: 1,
        }),
      )
    }
  }

  const onBoxClick = () => {
    if (isCopying) {
      const histories = localCropDataList?.at(-1)?.history as LocalSessionCropData[]
      const lastHistory = histories?.[histories.length - 1]

      if (lastHistory) {
        const alreadyCopied = histories?.some((item) => item.idfData === activityData.idfData)
        if (alreadyCopied) {
          return
        }

        const { cropStartTime, cropEndTime } = lastHistory
        const { dateStart, dateFinish } = activityData

        if (
          isBefore(new Date(cropEndTime), new Date(dateStart)) ||
          isAfter(new Date(cropStartTime), new Date(dateFinish))
        ) {
          return
        }

        const startTime = max([new Date(cropStartTime), new Date(dateStart)])
        const finishTime = min([new Date(cropEndTime), new Date(dateFinish)])

        const cropDuration = finishTime.getTime() - startTime.getTime()
        if (cropDuration <= 0) {
          return
        }

        addCropDataToLastHistory?.({
          ...lastHistory,
          cropStartTime: startTime.toString(),
          cropEndTime: finishTime.toString(),
          idfData: activityData.idfData,
          localIdfCropNumber: `temp-${Math.random().toString(36).substring(2, 15)}`,
        })
      }

      return
    }

    // 다른 선수의 타임라인을 클릭한 경우: 이전 temp history 정리
    if (activeCropIdfData !== undefined && activeCropIdfData !== activityData.idfData) {
      // 이전 선수의 temp history가 있으면 pop
      if (tempCropDataList && tempCropDataList.length > 0) {
        popHistoryStack?.()
      }
      // 새 선수로 전환 - temp 상태 초기화하고 새로 시작
      setTempStartX(sessionMouseGuideLine?.x || 0)
      setTempStartDate(sessionMouseGuideLine?.date as Date)
      setTempCropStartDate(sessionMouseGuideLine?.date as Date)
      setTempEndX(undefined)
      setTempEndDate(undefined)
      setActiveCropIdfData(activityData.idfData)

      return
    }

    if (tempEndX && tempStartX) {
      popHistoryStack?.()
      setTempStartX(sessionMouseGuideLine?.x || 0)
      setTempStartDate(sessionMouseGuideLine?.date as Date)
      setTempCropStartDate(sessionMouseGuideLine?.date as Date)
      setTempEndX(() => undefined)
      setTempEndDate(() => undefined)

      return
    }

    if (isNil(tempStartX)) {
      setTempStartX(sessionMouseGuideLine?.x || 0)
      setTempStartDate(sessionMouseGuideLine?.date as Date)
      setTempCropStartDate(sessionMouseGuideLine?.date as Date)
      setActiveCropIdfData(activityData.idfData)

      return
    }

    if (isNil(tempEndX)) {
      setTempEndX(sessionMouseGuideLine?.x || 0)
      setTempEndDate(sessionMouseGuideLine?.date as Date)
      setTempCropEndDate(sessionMouseGuideLine?.date as Date)

      return
    }
  }

  const onClickRemoveCrop = (record: LocalSessionCropData) => {
    const actionType = CropType.delete
    const history = [
      {
        ...record,
      },
    ]

    setDeleteHistory?.({
      actionType,
      history,
    })
  }

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown)

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  useEffect(() => {
    drawSpeedDataByWorker({ height: deviceHeight - speedChartPadding * 2 })
  }, [speedDataResponse])

  useEffect(() => {
    setTimeout(() => {
      const width = ref.current?.getBoundingClientRect().width || 0
      setWidth(() => width)

      if (tempStartDate) {
        setTempStartX(
          getGuideLinePosition({
            date: tempStartDate,
            tickStartDate: tickStartDate as Date,
            tickFinishDate: tickFinishDate as Date,
            width,
            originalWidth: 1,
          }),
        )
      }

      if (tempEndDate) {
        setTempEndX(
          getGuideLinePosition({
            date: tempEndDate,
            tickStartDate: tickStartDate as Date,
            tickFinishDate: tickFinishDate as Date,
            width,
            originalWidth: 1,
          }),
        )
      }
    })
  }, [magnification, tickStartDate, tickFinishDate])

  useEffect(() => {
    if (ref.current) {
      const observer = new IntersectionObserver((entries) => {
        setIsIntersect(entries[0].isIntersecting)
      })
      observer.observe(ref.current as Element)

      return () => {
        observer.disconnect()
      }
    }
  }, [ref.current])

  useEffect(() => {
    drawSpeedDataByWorker({ height: deviceHeight - speedChartPadding * 2 })
  }, [chartXSpace])

  useEffect(() => {
    setSessionMouseGuideLine({
      x: mouseGuidelineX,
      date: mouseGuidelineDate as Date,
      isVisible: false,
    })
  }, [mouseGuidelineDate, mouseGuidelineX])

  useEffect(() => {
    if (!isNil(tempEndX)) {
      const cropDuration =
        new Date(tempEndDate as Date).getTime() - new Date(tempStartDate as Date).getTime()

      if (cropDuration <= 0) {
        setTempStartX(undefined)
        setTempEndX(undefined)
        setTempStartDate(undefined)
        setTempEndDate(undefined)

        return
      }

      addHistoryStack?.({
        localIdfCropNumber: `temp-${Math.random().toString(36).substring(2, 15)}`,
        actionType: CropType.add,
        history: [
          {
            idfData: activityData.idfData,
            cropStartTime: tempStartDate?.toString() as string,
            cropEndTime: tempEndDate?.toString() as string,
            idfSessionCropRecord: 0,
            localIdfCropNumber: `temp-${Math.random().toString(36).substring(2, 15)}`,
            idfSession: idfSession || 0,
          },
        ],
      })

      // temp 상태는 유지 (가이드라인으로 계속 표시)
      // Crop 버튼을 누르면 tempCropDataList가 비워지고, 그때 temp 초기화
    }
  }, [tempEndX, tempEndDate])

  // Crop 버튼 클릭 시 tempCropDataList가 비워지면 temp 상태 초기화
  // 조건: tempEndX가 있을 때만 (범위 선택이 완료된 상태에서만)
  // 다시 클릭 시에는 onBoxClick에서 tempEndX가 undefined로 먼저 설정되므로 초기화되지 않음
  useEffect(() => {
    if (tempCropDataList?.length === 0 && tempEndX) {
      setTempStartX(undefined)
      setTempEndX(undefined)
      setTempStartDate(undefined)
      setTempEndDate(undefined)
    }
  }, [tempCropDataList?.length])

  useEffect(() => {
    if (isEscapePressed) {
      setTempStartX(undefined)
      setTempEndX(undefined)
      setTempStartDate(undefined)
      setTempEndDate(undefined)

      if (!isNil(tempStartX)) {
        popHistoryStack?.()
      }

      setIsEscapePressed(false)
    }
  }, [isEscapePressed])

  useEffect(() => {
    if (lastCropAction === CropType.cancel) {
      const cropData = localCropDataList?.at(-1)?.history[0]

      if (cropData && cropData.idfData === activityData.idfData) {
        const { cropStartTime, cropEndTime } = cropData

        const startX = getXPositionByDate(
          new Date(cropStartTime),
          new Date(session?.dateStart as string),
          new Date(session?.dateFinish as string),
        )
        const endX = getXPositionByDate(
          new Date(cropEndTime),
          new Date(session?.dateStart as string),
          new Date(session?.dateFinish as string),
        )

        setTempStartX(startX * width)
        setTempEndX(endX * width)
        setTempStartDate(new Date(cropStartTime))
        setTempCropStartDate(new Date(cropStartTime))
        setTempEndDate(new Date(cropEndTime))
        setTempCropEndDate(new Date(cropEndTime))

        popHistoryStackDirect?.()
      }
    }

    if (lastCropAction === CropType.copyAll) {
      const copyAllId = localCropDataList?.at(-1)?.localIdfCropNumber

      if (copyAllId && copyAllId !== lastProcessedCopyAll) {
        setLastProcessedCopyAll(copyAllId)

        const histories = localCropDataList?.at(-1)?.history as LocalSessionCropData[]
        const alreadyCopied = histories?.some((item) => item.idfData === activityData.idfData)
        if (alreadyCopied) {
          return
        }

        const { dateStart, dateFinish } = activityData

        if (
          isBefore(tempCropEndDate as Date, new Date(dateStart)) ||
          isAfter(tempCropStartDate as Date, new Date(dateFinish))
        ) {
          return
        }

        const startTime = max([tempCropStartDate as Date, new Date(dateStart)])
        const finishTime = min([tempCropEndDate as Date, new Date(dateFinish)])

        const cropDuration = finishTime.getTime() - startTime.getTime()
        if (cropDuration <= 0) {
          return
        }

        addCropDataToLastHistory?.({
          cropStartTime: startTime.toString(),
          cropEndTime: finishTime.toString(),
          idfData: activityData.idfData,
          idfSessionCropRecord: 0,
          localIdfCropNumber: `temp-${Math.random().toString(36).substring(2, 15)}`,
          idfSession: idfSession || 0,
        })
      }
    }
  }, [lastCropAction, localCropDataList])

  return (
    <svg
      className={classNames("DeviceTimelineCont", className)}
      width={timelineWidth + remainRightArea}
      height="98px"
      ref={ref}
      {...props}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setIsMouseOver(false)}>
      {points ? (
        <g
          transform={`translate(${(chartDataX?.start || 0) + remainRightArea / 2} , 20)`}
          height={deviceHeight - speedChartPadding * 2}>
          <polyline
            points={points}
            style={{ fill: "none", stroke: colors.blue["1000"], strokeWidth: 1 }}
          />
        </g>
      ) : (
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle">
          loading...
        </text>
      )}
      <line
        x1="0"
        x2="100%"
        y1="100%"
        y2="100%"
        stroke={colors.neutral.grey["300"]}
        strokeWidth={1}
      />

      {sessionMouseGuideLine && isMouseOver && (
        <g transform={`translate(${sessionMouseGuideLine.x - 1}, 0)`}>
          <line x1="0" y1="2" y2="100%" stroke="black" strokeWidth={1} strokeDasharray="2" />
          <rect
            width="52"
            height="16"
            x={isNil(tempStartX) || (!!tempStartX && !!tempEndX) ? "-50" : "0"}
            y="80"
            rx="3"
            ry="3"
            fill="black"
          />
          <text
            className="guideline-text"
            dominantBaseline="middle"
            textAnchor={isNil(tempStartX) || (!!tempStartX && !!tempEndX) ? "end" : "start"}
            x={isNil(tempStartX) || (!!tempStartX && !!tempEndX) ? "0" : "4"}
            y="89"
            fill="white"
            fontSize={11}
            fontWeight={600}
            fontFamily="Montserrat">
            {sessionMouseGuideLine.date &&
              format(sessionMouseGuideLine.date, HOUR_TO_SECOND_FORMAT)}
          </text>
        </g>
      )}
      {tempStartX && activeCropIdfData === activityData.idfData && (
        <g>
          <rect
            transform={`translate(${tempStartX}, 0)`}
            width={Math.max(0, tempEndX ? tempEndX - tempStartX : mouseGuidelineX - tempStartX)}
            height="100%"
            fill="black"
            opacity="0.12"></rect>
          <>
            <line
              transform={`translate(${tempStartX}, 0)`}
              x1="0"
              y1="2"
              y2="100%"
              strokeWidth={1}
              className="SessionGuideLine__line"
            />
            <rect
              transform={`translate(${tempStartX}, 0)`}
              width="52"
              height="16"
              x={-50}
              y={80}
              rx="3"
              ry="3"
              fill="black"
            />
            <text
              transform={`translate(${tempStartX}, 0)`}
              className="guideline-text"
              dominantBaseline="middle"
              textAnchor="end"
              x={0}
              y={89}
              fill="white"
              fontSize={11}
              fontWeight={600}
              fontFamily="Montserrat">
              {tempStartDate && format(tempStartDate, HOUR_TO_SECOND_FORMAT)}
            </text>
          </>
          {tempEndDate && (
            <>
              <line
                transform={`translate(${tempEndX}, 0)`}
                x1="0"
                y1="2"
                y2="100%"
                strokeWidth={1}
                className="SessionGuideLine__line"
              />
              <rect
                transform={`translate(${tempEndX}, 0)`}
                width="52"
                height="16"
                x={0}
                y={80}
                rx="3"
                ry="3"
                fill="black"
              />
              <text
                transform={`translate(${tempEndX}, 0)`}
                className="guideline-text"
                dominantBaseline="middle"
                textAnchor="start"
                x={4}
                y={89}
                fill="white"
                fontSize={11}
                fontWeight={600}
                fontFamily="Montserrat">
                {format(tempEndDate, HOUR_TO_SECOND_FORMAT)}
              </text>
            </>
          )}
        </g>
      )}
      {sessionData && (
        <DeviceCropItemBox
          {...sessionData}
          dateStart={activityData?.dateStart}
          dateFinish={activityData?.dateFinish}
          id={idfSession ?? ""}
          textId={`${idfSession}_editable`}
          editable
          startX={dataX?.start || 0}
          width={(dataX?.end || 0) - (dataX?.start || 0)}
          startY={16}
          height={68}
          cropData={mergedCropDataList}
          idfData={activityData.idfData}
          onSessionBoxClick={onBoxClick}
          onCropBoxClick={onClickRemoveCrop}
        />
      )}
    </svg>
  )
}
